import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Bell,
  ChevronDown,
  CreditCard,
  Settings,
  BookOpen,
  LogOut,
  LayoutDashboard,
  Link2,
  Target,
  Layers,
  UserSquare2,
  Palette,
  BarChart3,
  Key,
  Webhook,
  Plug,
  Users,
  Globe,
  CornerDownLeft,
} from "lucide-react";
import { CandyButton } from "../playful/CandyButton";
import { IconButton } from "../playful/IconButton";
import { useAuth } from "../../context/AuthContext";
import { usePlan } from "../../hooks/usePlan";
import { useNotifications } from "../../hooks/useNotifications";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Searchable app sections — type to jump. `keywords` widen matching.
const SECTIONS = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    to: "/dashboard",
    group: "Overview",
    keywords: "home overview stats",
  },
  {
    label: "Links",
    icon: Link2,
    to: "/links",
    group: "Link Management",
    keywords: "shorten url lanes create",
  },
  {
    label: "Campaigns",
    icon: Target,
    to: "/campaigns",
    group: "Link Management",
    keywords: "utm group rollup",
  },
  {
    label: "Bulk Operations",
    icon: Layers,
    to: "/bulk",
    group: "Link Management",
    keywords: "csv import export batch",
  },
  {
    label: "Bio Pages",
    icon: UserSquare2,
    to: "/bio/edit",
    group: "Creator Tools",
    keywords: "link in bio profile",
  },
  {
    label: "Theme Studio",
    icon: Palette,
    to: "/bio/edit?tab=design",
    group: "Creator Tools",
    keywords: "design colors theme bio",
  },
  {
    label: "Analytics",
    icon: BarChart3,
    to: "/analytics",
    group: "Analytics",
    keywords: "stats clicks charts insights",
  },
  {
    label: "API Keys",
    icon: Key,
    to: "/api-keys",
    group: "Automation",
    keywords: "developer token rest",
  },
  {
    label: "Webhooks",
    icon: Webhook,
    to: "/webhooks",
    group: "Automation",
    keywords: "events hooks notify",
  },
  {
    label: "Integrations",
    icon: Plug,
    to: "/integrations",
    group: "Automation",
    keywords: "slack discord connect",
  },
  { label: "Team", icon: Users, to: "/team", group: "Workspace", keywords: "members invite seats" },
  {
    label: "Domains",
    icon: Globe,
    to: "/domains",
    group: "Workspace",
    keywords: "custom host dns",
  },
  {
    label: "Billing",
    icon: CreditCard,
    to: "/billing",
    group: "Workspace",
    keywords: "plan subscription invoice upgrade",
  },
  {
    label: "Settings",
    icon: Settings,
    to: "/settings",
    group: "Workspace",
    keywords: "account profile password",
  },
  {
    label: "Docs",
    icon: BookOpen,
    to: "https://docs.linklane.io",
    group: "Workspace",
    keywords: "help api reference guide",
  },
];

function filterSections(query) {
  const q = query.trim().toLowerCase();
  if (!q) return SECTIONS;
  return SECTIONS.filter((s) => s.label.toLowerCase().includes(q) || s.keywords.includes(q));
}

const PLAN_BADGE = {
  admin: { label: "ADMIN", cls: "bg-candy/15 text-candy border-candy/30" },
  enterprise: { label: "ENT", cls: "bg-mint/20 text-ink border-mint/30" },
  growth: { label: "GROWTH", cls: "bg-pink/20 text-ink border-pink/40" },
  pro: { label: "PRO", cls: "bg-yellow/30 text-ink border-yellow/40" },
  free: { label: "FREE", cls: "bg-ink/8 text-ink/50 border-ink/15" },
};

/** Topbar — desktop application header. Workspace · search · create · notifications · profile. */
export default function Topbar({ onOpenCreate }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { plan, isAdmin } = usePlan();
  const { items: notifs, unread, markAllRead } = useNotifications();
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const bellRef = useRef(null);

  const results = filterSections(q);

  const badge = PLAN_BADGE[isAdmin ? "admin" : plan] || PLAN_BADGE.free;
  const initial =
    user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "?";

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const goToSection = (section) => {
    if (!section) return;
    setQ("");
    setSearchOpen(false);
    if (/^https?:\/\//.test(section.to)) {
      window.location.href = section.to; // external (e.g. docs subdomain)
    } else {
      navigate(section.to);
    }
  };

  const onSearchKeyDown = (e) => {
    if (!searchOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
      setSearchOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      goToSection(results[activeIdx]);
    } else if (e.key === "Escape") {
      setSearchOpen(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-30 hidden h-[65px] shrink-0 items-center gap-4 border-b-2 border-ink bg-white/90 px-6 backdrop-blur-sm md:flex"
      data-testid="app-topbar"
    >
      {/* Workspace */}
      <div className="flex shrink-0 items-center gap-2">
        <span className="font-display text-lg font-extrabold">Workspace</span>
        <span
          className={`rounded-full border px-1.5 py-0.5 font-mono text-2xs font-bold uppercase tracking-[0.22em] ${badge.cls}`}
        >
          {badge.label}
        </span>
      </div>

      {/* Section navigator */}
      <div ref={searchRef} className="relative mx-auto max-w-md flex-1">
        <div className="flex items-center gap-2 rounded-full border-2 border-ink bg-paper px-4 py-2 shadow-sticker-xs transition-shadow focus-within:shadow-sticker">
          <Search size={15} strokeWidth={2.5} className="shrink-0 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActiveIdx(0);
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search sections…"
            role="combobox"
            aria-expanded={searchOpen}
            aria-controls="topbar-section-list"
            className="min-w-0 flex-1 border-0 bg-transparent font-sans text-sm placeholder:text-muted-foreground"
            data-testid="topbar-search"
          />
          <kbd className="hidden rounded border border-ink/20 px-1.5 py-0.5 font-mono text-2xs tracking-widest text-ink/60 lg:inline">
            ⌘K
          </kbd>
        </div>

        {searchOpen && (
          <div
            id="topbar-section-list"
            role="listbox"
            className="pop-in absolute left-0 right-0 top-full z-40 mt-2 max-h-[60vh] overflow-y-auto rounded-2xl border-2 border-ink bg-white p-2 shadow-sticker-md"
            data-testid="topbar-section-results"
          >
            {results.length === 0 ? (
              <p className="px-3 py-4 text-center font-mono text-2xs uppercase tracking-[0.22em] text-muted-foreground">
                — NO SECTION —
              </p>
            ) : (
              results.map((s, i) => {
                const Icon = s.icon;
                const active = i === activeIdx;
                return (
                  <button
                    key={s.to}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => goToSection(s)}
                    data-testid={`topbar-section-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors ${
                      active ? "bg-candy-tint" : "hover:bg-ink/5"
                    }`}
                  >
                    <Icon size={16} strokeWidth={2} className="shrink-0 text-candy" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-display text-sm font-semibold text-ink">
                        {s.label}
                      </span>
                      <span className="block font-mono text-2xs uppercase tracking-[0.18em] text-muted-foreground">
                        {s.group}
                      </span>
                    </span>
                    {active && (
                      <CornerDownLeft
                        size={13}
                        strokeWidth={2.5}
                        className="shrink-0 text-muted-foreground"
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Right cluster */}
      <div className="flex shrink-0 items-center gap-2">
        <CandyButton
          variant="candy"
          size="sm"
          icon={Plus}
          onClick={onOpenCreate}
          data-testid="topbar-create"
        >
          Create
        </CandyButton>

        {/* Notifications */}
        <div className="relative" ref={bellRef}>
          <div className="relative">
            <IconButton
              icon={Bell}
              tone="paper"
              onClick={() => {
                setBellOpen((v) => !v);
                if (!bellOpen && unread > 0) markAllRead();
              }}
              aria-label="Notifications"
              data-testid="topbar-bell"
            />
            {unread > 0 && (
              <span
                className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white bg-pink px-1 font-mono text-[9px] font-bold text-white"
                data-testid="notif-badge"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </div>
          {bellOpen && (
            <div className="pop-in absolute right-0 top-full mt-2 w-80 overflow-hidden rounded-2xl border-2 border-ink bg-white shadow-sticker-md">
              <div className="flex items-center justify-between border-b-2 border-ink/10 px-4 py-3">
                <span className="font-display text-sm font-extrabold">Notifications</span>
                {notifs.length > 0 && (
                  <button
                    onClick={markAllRead}
                    className="font-mono text-2xs uppercase tracking-[0.18em] text-candy hover:underline"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              {notifs.length === 0 ? (
                <div className="px-5 py-8 text-center">
                  <p className="font-mono text-2xs uppercase tracking-[0.22em] text-muted-foreground">
                    — ALL CLEAR —
                  </p>
                  <p className="mt-2 font-sans text-sm text-ink/60">
                    No notifications yet. Activity will appear here.
                  </p>
                </div>
              ) : (
                <ul className="max-h-96 overflow-y-auto" data-testid="notif-list">
                  {notifs.map((n) => (
                    <li key={n.id}>
                      <button
                        onClick={() => {
                          setBellOpen(false);
                          if (n.url) navigate(n.url);
                        }}
                        className={`border-ink/8 flex w-full items-start gap-3 border-b px-4 py-3 text-left transition-colors hover:bg-paper ${n.read ? "" : "bg-candy/5"}`}
                      >
                        <span
                          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-candy"}`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-sans text-sm font-semibold text-ink">
                            {n.title}
                          </span>
                          {n.body ? (
                            <span className="block font-sans text-xs text-ink/60">{n.body}</span>
                          ) : null}
                          <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                            {timeAgo(n.created_at)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Profile */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="candy-press flex items-center gap-1.5 rounded-full border-2 border-ink bg-white py-1 pl-1 pr-2 shadow-sticker-xs"
            data-testid="topbar-profile"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-ink bg-candy font-display text-xs font-bold text-white">
              {initial}
            </span>
            <ChevronDown
              size={13}
              strokeWidth={3}
              className={`transition-transform ${menuOpen ? "rotate-180" : ""}`}
            />
          </button>
          {menuOpen && (
            <div className="pop-in absolute right-0 top-full mt-2 w-60 rounded-2xl border-2 border-ink bg-white p-2 shadow-sticker-md">
              <div className="mb-1 border-b border-ink/10 px-3 py-2">
                <p className="truncate font-mono text-2xs uppercase tracking-[0.14em] text-ink/60">
                  {user?.email}
                </p>
              </div>
              {[
                { icon: CreditCard, label: "Billing", to: "/billing" },
                { icon: Settings, label: "Settings", to: "/settings" },
                { icon: BookOpen, label: "Docs", to: "https://docs.linklane.io" },
              ].map(({ icon: Icon, label, to }) => (
                <button
                  key={label}
                  onClick={() => {
                    setMenuOpen(false);
                    if (/^https?:\/\//.test(to)) {
                      window.location.href = to;
                    } else {
                      navigate(to);
                    }
                  }}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-ink/70 transition-colors hover:bg-ink/5 hover:text-ink"
                >
                  <Icon size={15} strokeWidth={2} /> {label}
                </button>
              ))}
              <button
                onClick={onLogout}
                className="hover:bg-pink/8 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-ink/60 transition-colors hover:text-pink"
              >
                <LogOut size={15} strokeWidth={2} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
